import { useState } from "react";
import { Money } from "@shop-ledger/shared-types";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getSalesSummary } from "../../api/reportsApi";
import type { TopSellerOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

const SELLER_COLUMNS: DataTableColumn<TopSellerOut, keyof TopSellerOut>[] = [
  { key: "sku", header: "SKU", render: (r) => r.sku },
  { key: "name", header: "Name", render: (r) => r.name },
  { key: "quantity_sold", header: "Qty sold", align: "right", render: (r) => r.quantity_sold },
  { key: "revenue", header: "Revenue", align: "right", render: (r) => Money.format(r.revenue, TENANT_CURRENCY_FALLBACK) },
];

/** `GET /reports/sales-summary?branch_id=&date_from=&date_to=` (§10.5) — revenue, sale
 * count, average basket size (defined server-side as revenue / sale count — the
 * standard retail-analytics meaning), and top sellers by quantity/revenue. */
export function SalesDashboardPage() {
  const { ensureFreshToken } = useAuth();
  const [branchId, setBranchId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const state = useAsyncData(
    () =>
      ensureFreshToken().then((token) =>
        getSalesSummary(token, {
          branchId: branchId || undefined,
          dateFrom: dateFrom ? `${dateFrom}T00:00:00` : undefined,
          dateTo: dateTo ? `${dateTo}T23:59:59` : undefined,
        }),
      ),
    [branchId, dateFrom, dateTo],
  );

  const summary = state.data;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Sales Dashboard</h1>
          <p className="page__subtitle">Revenue, basket size, and top sellers over a date range.</p>
        </div>
      </div>

      {state.error ? <StatusBanner kind="error">{state.error}</StatusBanner> : null}

      <div className="filter-bar">
        <BranchSelect id="sales-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="sales-from">
            From
          </label>
          <input id="sales-from" className="sl-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="sales-to">
            To
          </label>
          <input id="sales-to" className="sl-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      {state.loading || !summary ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="stat-row">
            <div className="stat-tile">
              <p className="stat-tile__label">Revenue</p>
              <p className="stat-tile__value stat-tile__value--positive">{Money.format(summary.revenue_total, TENANT_CURRENCY_FALLBACK)}</p>
            </div>
            <div className="stat-tile">
              <p className="stat-tile__label">Sale count</p>
              <p className="stat-tile__value">{summary.sale_count}</p>
            </div>
            <div className="stat-tile">
              <p className="stat-tile__label">Average basket size</p>
              <p className="stat-tile__value">{Money.format(summary.average_basket_size, TENANT_CURRENCY_FALLBACK)}</p>
            </div>
          </div>

          <div className="panel">
            <h2 className="panel__title">Top sellers by quantity</h2>
            <DataTable columns={SELLER_COLUMNS} rows={summary.top_sellers_by_quantity} getRowId={(row) => row.inventory_item_id} emptyMessage="No sales in this range." />
          </div>

          <div className="panel">
            <h2 className="panel__title">Top sellers by revenue</h2>
            <DataTable columns={SELLER_COLUMNS} rows={summary.top_sellers_by_revenue} getRowId={(row) => row.inventory_item_id} emptyMessage="No sales in this range." />
          </div>
        </>
      )}
    </div>
  );
}
